/**
 * Documents (storefront): the product page's Specifications tab — the product's datasheets and specifications as
 * a list, each an icon and its name. A PDF of ours opens in the site's PDF viewer (the buyer stays on the page);
 * another file, or one left on its maker's site, opens as a link. No documents, no tab (`visible`).
 */
import React, { useState } from "react"
import { ArrowTopRightOnSquareIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import useT from "../../../Storefront/hooks/useT"
import DocumentViewer from "./DocumentViewer"

const isPdf = (document) => document?.mime === "application/pdf" || /\.pdf(\?|$)/i.test(document?.url || "")
const sizeOf = (bytes) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : bytes > 0 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : "")

function SpecificationsTab({ product }) {
  const t = useT()
  const [viewing, setViewing] = useState(null)
  const documents = Array.isArray(product?.documents) ? product.documents : []
  if (!documents.length) return <p className="text-text_secondary">{t`No documents for this product.`}</p>
  const texts = { loading: t`Loading…`, error: t`Could not load the document`, download: t`Download`, zoomIn: t`Zoom in`, zoomOut: t`Zoom out`, fitWidth: t`Fit width`, prevPage: t`Previous page`, nextPage: t`Next page`, pages: t`Pages`, showPages: t`Show the pages`, hidePages: t`Hide the pages`, pageN: t`Page %s`, search: t`Search in the document`, prevMatch: t`Previous match`, nextMatch: t`Next match`, noMatches: t`No matches` }
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-2" data-testid="specifications-tab">
      {documents.map((document) => {
        const inViewer = isPdf(document) && !document.external
        const label = (
          <>
            <span className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <DocumentTextIcon className="w-5 h-5" />
            </span>
            <span className="flex-1 min-w-0 flex flex-col">
              <span className="font-semibold text-text truncate group-hover:text-primary group-hover:underline underline-offset-4">{document.name}</span>
              <span className="text-[12px] text-text_secondary">{[isPdf(document) ? "PDF" : "", sizeOf(document.size)].filter(Boolean).join(" · ")}</span>
            </span>
            {!inViewer && <ArrowTopRightOnSquareIcon className="w-4 h-4 text-text_secondary shrink-0" />}
          </>
        )
        const row = "group w-full flex items-center gap-3 p-3 rounded-xl border border-bordered hover:border-primary/50 bg-paper text-left transition-colors"
        return (
          <li key={document.url}>
            {inViewer ? (
              <button type="button" className={row} onClick={() => setViewing(document)} data-testid="specifications-document">
                {label}
              </button>
            ) : (
              <a className={row} href={document.url} target="_blank" rel="noopener noreferrer" data-testid="specifications-document">
                {label}
              </a>
            )}
          </li>
        )
      })}
      {viewing && <DocumentViewer document={viewing} texts={texts} onClose={() => setViewing(null)} />}
    </ul>
  )
}

export const StorefrontExtension = {
  id: "documents",
  label: "Specifications",
  // no documents, no tab
  slots: { tabs: { id: "documents", label: "Specifications", component: SpecificationsTab, visible: (product) => Array.isArray(product?.documents) && product.documents.length > 0 } },
}
